import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getFeaturedListings, getCountries, getCities, aiSearch } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ListingCard from '../components/ListingCard';
import WelcomePopup from '../components/WelcomePopup';
import { Search, Sparkles, Home, Building, Hotel, DoorOpen, MapPin, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const HomePage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [featuredListings, setFeaturedListings] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [aiMode, setAiMode] = useState(false);
  const [aiResponse, setAiResponse] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      loadCities(selectedCountry);
    } else {
      setCities([]);
      setSelectedCity('');
    }
  }, [selectedCountry]);

  const loadInitialData = async () => {
    try {
      const [listingsRes, countriesRes] = await Promise.all([
        getFeaturedListings(6),
        getCountries()
      ]);
      setFeaturedListings(listingsRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async (countryId) => {
    try {
      const res = await getCities(countryId);
      setCities(res.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedCity) params.set('city', cities.find(c => c.city_id === selectedCity)?.name || '');
    if (selectedType) params.set('type', selectedType);
    navigate(`/listings?${params.toString()}`);
  };

  const handleAISearch = async () => {
    if (!searchQuery.trim()) return;
    
    setAiLoading(true);
    try {
      const res = await aiSearch(searchQuery, language);
      setAiResponse(res.data.response);
      if (res.data.suggested_listing_ids?.length > 0) {
        toast.success('Logements trouvés !');
      }
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setAiLoading(false);
    }
  };

  const listingTypes = [
    { value: 'apartment', label: t('apartment'), icon: Building },
    { value: 'studio', label: t('studio'), icon: Home },
    { value: 'room', label: t('room'), icon: DoorOpen },
    { value: 'hotel', label: t('hotel'), icon: Hotel },
  ];

  const popularCities = [
    { name: 'Yaoundé', image: 'https://images.unsplash.com/photo-1659947234309-804b7fa01cf2?w=800' },
    { name: 'Douala', image: 'https://images.unsplash.com/photo-1659947234294-b217aaeb25f8?w=800' },
    { name: 'Kribi', image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800' },
  ];

  return (
    <div className="min-h-screen">
      {/* Welcome Popup for Launch */}
      <WelcomePopup />
      
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1758548157747-285c7012db5b?w=1920)` 
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
            {t('heroTitle')}
          </h1>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            {t('heroSubtitle')}
          </p>

          {/* Search Box */}
          <Card className="glass max-w-4xl mx-auto" data-testid="hero-search">
            <CardContent className="p-6">
              {/* Toggle AI Mode */}
              <div className="flex justify-center mb-4">
                <Button
                  variant={aiMode ? "default" : "outline"}
                  onClick={() => setAiMode(!aiMode)}
                  className="gap-2"
                  data-testid="ai-search-toggle"
                >
                  <Sparkles className="h-4 w-4" />
                  Recherche IA
                </Button>
              </div>

              {aiMode ? (
                <div className="space-y-4">
                  <div className="relative">
                    <Input
                      placeholder={t('aiSearchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-14 pl-12 pr-4 text-lg rounded-full border-2 border-primary/20 focus:border-primary focus:ring-4 focus:ring-primary/10"
                      onKeyDown={(e) => e.key === 'Enter' && handleAISearch()}
                      data-testid="ai-search-input"
                    />
                    <Sparkles className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary" />
                  </div>
                  <Button 
                    onClick={handleAISearch} 
                    disabled={aiLoading}
                    className="w-full h-12 rounded-full text-lg gap-2"
                    data-testid="ai-search-btn"
                  >
                    {aiLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5" />
                        {t('search')}
                      </>
                    )}
                  </Button>
                  {aiResponse && (
                    <div className="mt-4 p-4 bg-secondary rounded-xl text-left" data-testid="ai-response">
                      <p className="text-sm whitespace-pre-wrap">{aiResponse}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Select value={selectedCountry} onValueChange={(val) => {
                      setSelectedCountry(val);
                      setSelectedCity('');
                    }}>
                      <SelectTrigger data-testid="country-select">
                        <SelectValue placeholder={t('country')} />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country.country_id} value={country.country_id}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select 
                      value={selectedCity} 
                      onValueChange={setSelectedCity} 
                      disabled={!selectedCountry || cities.length === 0}
                    >
                      <SelectTrigger data-testid="city-select" className={!selectedCountry ? 'opacity-50' : ''}>
                        <SelectValue placeholder={t('city')} />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city.city_id} value={city.city_id}>
                            {city.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedType} onValueChange={setSelectedType}>
                      <SelectTrigger data-testid="type-select">
                        <SelectValue placeholder={t('allTypes')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('allTypes')}</SelectItem>
                        {listingTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    onClick={handleSearch}
                    className="w-full h-12 rounded-full text-lg gap-2"
                    data-testid="search-btn"
                  >
                    <Search className="h-5 w-5" />
                    {t('search')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Listing Types */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {listingTypes.map((type) => {
              const Icon = type.icon;
              return (
                <Link
                  key={type.value}
                  to={`/listings?type=${type.value}`}
                  className="group"
                  data-testid={`type-card-${type.value}`}
                >
                  <Card className="h-full hover:shadow-hover hover:-translate-y-1 transition-all duration-300 border-border/50 hover:border-primary/30">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="font-heading font-semibold">{type.label}</h3>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Listings */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold" data-testid="featured-title">
              {t('featuredListings')}
            </h2>
            <Link to="/listings">
              <Button variant="outline" className="gap-2" data-testid="view-all-btn">
                {t('viewAll')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-muted"></div>
                  <CardContent className="p-4 space-y-3">
                    <div className="h-6 bg-muted rounded"></div>
                    <div className="h-4 bg-muted rounded w-2/3"></div>
                    <div className="h-8 bg-muted rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-listings">
              {featuredListings.map((listing) => (
                <ListingCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Popular Cities */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-10" data-testid="cities-title">
            {t('popularCities')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {popularCities.map((city, index) => (
              <Link
                key={city.name}
                to={`/listings?city=${city.name}`}
                className={`group relative overflow-hidden rounded-2xl ${index === 0 ? 'md:row-span-2' : ''}`}
                data-testid={`city-card-${city.name.toLowerCase()}`}
              >
                <div className={`relative ${index === 0 ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}>
                  <img
                    src={city.image}
                    alt={city.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 p-6">
                    <div className="flex items-center gap-2 text-white">
                      <MapPin className="h-5 w-5" />
                      <h3 className="font-heading text-2xl font-bold">{city.name}</h3>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold mb-6">
            {language === 'fr' 
              ? 'Vous êtes propriétaire ?' 
              : 'Are you a property owner?'}
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Publiez vos annonces gratuitement et touchez des milliers de locataires potentiels au Cameroun et en Afrique.'
              : 'Post your listings for free and reach thousands of potential tenants in Cameroon and Africa.'}
          </p>
          <Link to="/auth?mode=register">
            <Button size="lg" variant="secondary" className="rounded-full px-8" data-testid="cta-register-btn">
              {language === 'fr' ? 'Commencer maintenant' : 'Get started now'}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
